import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  BookMarked,
  ArrowRight,
  Sparkles,
  Layers,
  MessageCircleQuestion,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  Compass,
  PieChart,
  Calculator,
  Lightbulb,
  PenLine,
  Percent,
} from "lucide-react";
import DiagramRenderer from "@/components/DiagramRenderer";

const SIDE_BUBBLE_MESSAGE =
  "Each concept builds on the last — Mitraa reveals one step at a time, just like in a real session.";
const ASK_MITRAA_MESSAGE =
  "Mitraa explains each concept with examples and language matched to the student's grade level and pace.";
const REVEAL_DELAY_MS = 400;

// Demo-only splitter — intentionally separate from the real engine's
// toBullets() / ReviseRecapRenderer. Splits a raw recap text block whenever a
// line starts with an ALL-CAPS phrase immediately followed by ":" or "(" —
// covers both standalone headers (e.g. "PERCENTAGE INCREASE:") and inline
// header+content lines (e.g. "KEY RULE: Always divide..."), each starting a
// new step that carries its content until the next header.
// Demo-only splitter — intentionally separate from the real engine's
// toBullets() / ReviseRecapRenderer. Two paths:
//  1) If `items` (pre-split single-concept bullets from revise_recap_content)
//     has entries, use them directly as steps — no header parsing needed.
//     Covers topics like Hindi recaps where `content` is one continuous
//     paragraph with no line breaks and no case distinction to detect headers.
//  2) Otherwise, fall back to the raw-text ALL-CAPS header splitting below
//     (English/Math topics where `items` is empty).
function splitDemoRecapSteps(text, items) {
  if (Array.isArray(items) && items.length > 0) {
    return items.map((it) => (it || "").trim()).filter(Boolean);
  }
  if (!text) return [];
  const HEADER_RE = /^([A-Z][A-Z0-9\- ]*?)\s*(:|\()/;
  const MEMORY_HOOK_RE = /^(Memory hook)\s*:/i;
  const steps = [];
  let current = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(HEADER_RE);
    const mh = !m ? line.match(MEMORY_HOOK_RE) : null;
    if (m) {
      if (current.length) steps.push(current.join("\n"));
      if (m[2] === ":") {
        const headerText = m[1].trim();
        const rest = line.slice(m[0].length).trim();
        current = rest ? [`${headerText}:`, rest] : [`${headerText}:`];
      } else {
        current = [line];
      }
      continue;
    }
    if (mh) {
      if (current.length) steps.push(current.join("\n"));
      const rest = line.slice(mh[0].length).trim();
      current = rest ? ["Memory hook:", rest] : ["Memory hook:"];
      continue;
    }
    current.push(line);
  }
  if (current.length) steps.push(current.join("\n"));
  return steps.length ? steps : (text.trim() ? [text.trim()] : []);
}

// Splits a step's raw text into its extracted header (if the first line is a
// header-style line) and the remaining body, so the two can be styled separately.
function parseStepHeader(step) {
  const lines = step.split("\n");
  const first = (lines[0] || "").trim();
  const isMemoryHookHeader = /^memory hook:$/i.test(first);
  if (isMemoryHookHeader) {
    return { header: "Memory hook", body: lines.slice(1).join("\n").trim() };
  }
  const isColonHeader = first.endsWith(":") && /[A-Z]/.test(first) && !/[a-z]/.test(first);
  if (isColonHeader) {
    return { header: first.replace(/:$/, ""), body: lines.slice(1).join("\n").trim() };
  }
  const isParenHeader = /^[A-Z][A-Z0-9\- ]*\(/.test(first);
  if (isParenHeader) {
    return { header: first, body: lines.slice(1).join("\n").trim() };
  }
  return { header: null, body: step };
}

// Maps a header's keywords to a representative Lucide icon for the tinted header block.
function pickHeaderIcon(header) {
  if (!header) return Sparkles;
  const t = header.toUpperCase();
  if (/MEMORY|HOOK/.test(t)) return Lightbulb;
  if (/INCREASE/.test(t)) return ArrowUpRight;
  if (/DECREASE/.test(t)) return ArrowDownRight;
  if (/RULE|KEY/.test(t)) return ShieldCheck;
  if (/METHOD/.test(t)) return Compass;
  if (/RATIO|PROPORTION/.test(t)) return PieChart;
  if (/FORMULA|CALCULAT/.test(t)) return Calculator;
  if (/DEFINITION|CONCEPT|MEANING/.test(t)) return Lightbulb;
  if (/EXAMPLE/.test(t)) return PenLine;
  if (/PERCENT/.test(t)) return Percent;
  return Sparkles;
}

export default function TryRecapScreen({ recap, onProceed }) {
  const content = recap?.content || recap?.body || "";
  const isItemsBased = Array.isArray(recap?.items) && recap.items.length > 0;
  const steps = useMemo(() => splitDemoRecapSteps(content, recap?.items), [content, recap?.items]);
  const total = steps.length;

  const [revealed, setRevealed] = useState(1);
  const [revealingAll, setRevealingAll] = useState(false);
  const [showAskMitraa, setShowAskMitraa] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setRevealed(1);
    setRevealingAll(false);
    setShowAskMitraa(false);
    return () => clearTimeout(timerRef.current);
  }, [recap?.record_id]);

  const revealNext = (current) => {
    const next = current + 1;
    setRevealed(next);
    if (next < total) {
      timerRef.current = setTimeout(() => revealNext(next), REVEAL_DELAY_MS);
    } else {
      setRevealingAll(false);
    }
  };

  const handleShowAll = () => {
    if (revealingAll || revealed >= total) return;
    setRevealingAll(true);
    revealNext(revealed);
  };

  const allRevealed = revealed >= total;

  return (
    <div className="fade-up max-w-2xl mx-auto" data-testid="try-recap-screen">
      <div className="text-center mb-5">
        <span
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wider"
          data-testid="try-recap-tag"
        >
          <BookMarked className="w-3.5 h-3.5" /> Recap · {recap?.subject}
        </span>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-800 mt-3" data-testid="try-recap-title">
          {recap?.title || "Quick Recap"}
        </h2>
      </div>

      {recap?.has_diagram && recap?.record_id && (
        <div className="mb-5">
          <DiagramRenderer recordId={recap.record_id} description={recap.diagram_description} />
        </div>
      )}

      <div
        className="flex items-start gap-3 rounded-2xl p-4 mb-5 bg-gradient-to-r from-violet-50 to-sky-50 border border-violet-100"
        data-testid="try-recap-mitraa-bubble"
      >
        <div
          className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-sky-500 flex items-center justify-center shrink-0"
          data-testid="try-recap-mitraa-avatar"
        >
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <p className="text-sm text-slate-700 leading-relaxed pt-1.5">{SIDE_BUBBLE_MESSAGE}</p>
      </div>

      <div className="space-y-4 mb-5" data-testid="try-recap-steps">
        {steps.slice(0, revealed).map((step, i) => {
          const { header, body } = parseStepHeader(step);
          const HeaderIcon = pickHeaderIcon(header);
          const isActive = i === revealed - 1;
          const isIntroCard = !isItemsBased && i === 0 && !header;
          return (
            <div
              key={i}
              className={`relative overflow-hidden rounded-2xl p-5 border fade-up mit-shadow ${
                isIntroCard
                  ? `bg-violet-50 border-violet-200 ${isActive ? "ring-2 ring-sky-300 shadow-lg" : ""}`
                  : isActive
                  ? "bg-white border-sky-200 ring-2 ring-sky-300 shadow-lg"
                  : "bg-sky-50/40 border-sky-100"
              }`}
              data-testid={`try-recap-step-${i}`}
            >
              <span
                className="absolute bottom-1 right-3 text-4xl md:text-5xl font-black text-violet-900/[0.07] select-none pointer-events-none leading-none"
                aria-hidden="true"
                data-testid={`try-recap-step-${i}-watermark`}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="relative z-10">
                {header && (
                  <span
                    className="inline-flex items-center gap-2 bg-violet-50 text-violet-900 font-semibold text-sm px-3 py-1.5 rounded-lg mb-2"
                    data-testid={`try-recap-step-${i}-header`}
                  >
                    <HeaderIcon className="w-4 h-4 text-violet-600 shrink-0" /> {header}
                  </span>
                )}
                <p
                  className={`leading-relaxed whitespace-pre-line ${
                    isIntroCard
                      ? "text-base md:text-lg font-semibold text-violet-900 text-center"
                      : "text-slate-700 text-sm md:text-base"
                  }`}
                >
                  {body}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {!allRevealed && (
        <div className="text-center mb-5">
          <Button
            onClick={handleShowAll}
            disabled={revealingAll}
            variant="outline"
            className="rounded-full border-2 border-sky-300 text-sky-700 hover:bg-sky-50 font-semibold tap-44 px-5 py-2"
            data-testid="try-recap-show-all-btn"
          >
            <Layers className="w-4 h-4 mr-1.5" /> {revealingAll ? "Revealing…" : "Show all steps"}
          </Button>
          <p className="text-xs text-slate-400 mt-2" data-testid="try-recap-progress">{revealed} of {total}</p>
        </div>
      )}

      <div className="text-center mb-6">
        {!showAskMitraa ? (
          <button
            type="button"
            onClick={() => setShowAskMitraa(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700 transition-colors"
            data-testid="try-recap-ask-mitraa-btn"
          >
            <MessageCircleQuestion className="w-4 h-4" /> Ask Mitraa
          </button>
        ) : (
          <div
            className="rounded-2xl p-4 bg-violet-50 border border-violet-100 text-sm text-violet-800 max-w-lg mx-auto fade-up"
            data-testid="try-recap-ask-mitraa-reply"
          >
            {ASK_MITRAA_MESSAGE}
          </div>
        )}
      </div>

      <div className="text-center">
        <Button
          onClick={onProceed}
          className="rounded-full bg-sky-400 hover:bg-sky-500 text-white text-base font-semibold px-8 py-6 mit-shadow tap-44"
          data-testid="try-recap-proceed-btn"
        >
          I'm ready <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
